import { CalendarEventParticipant } from '@/../types/interfaces';
import { CalendarEventParticipantResponse } from '@/../types/enums';
import { CalendarEventModel } from '@/models/calendar/CalendarEventModel';

/**
 * A repeating event is one record that the calendar draws on many dates, so availability for it
 * is stored per date. These helpers are the only place that decides whether an answer belongs to
 * a single date or to the whole series - keep reads and writes going through them so the two
 * never disagree.
 */

/** The key an occurrence is stored under: its UTC calendar date, formatted YYYY-MM-DD. */
export function occurrenceKey(date: Date): string {
	return date.toISOString().slice(0, 10);
}

/** True when the event repeats, and therefore holds a separate answer for each date. */
export function eventRepeats(event: CalendarEventModel): boolean {
	if (!event) return false;
	return event.recurrenceRule !== null || (Array.isArray(event.inclusionDates) && event.inclusionDates.length > 0);
}

/**
 * The date to send with an answer, or undefined for a one-off event. Passing undefined keeps the
 * answer on the participant, which is how every event created before per-date answers behaves.
 */
export function rsvpOccurrenceDate(event: CalendarEventModel, occurrence: Date): string | undefined {
	if (!eventRepeats(event)) return undefined;
	return occurrenceKey(occurrence);
}

/**
 * The occurrence key for the date a viewer is looking at, derived from the event's own start
 * time so it lands on the same key the calendar produces when it expands the series. The
 * availability grid and the event page must derive this identically or they will disagree
 * about which date an answer belongs to.
 */
export function occurrenceKeyForViewedDate(event: CalendarEventModel, viewedDate: Date): string | undefined {
	if (!eventRepeats(event)) return undefined;
	if (!viewedDate || isNaN(viewedDate.getTime())) return undefined;
	const occurrence = new Date(event.start);
	occurrence.setFullYear(viewedDate.getFullYear(), viewedDate.getMonth(), viewedDate.getDate());
	return occurrenceKey(occurrence);
}

/** The answer that applies on one date: the per-date entry if there is one, else the series answer. */
export function participantRsvpOn(participant: CalendarEventParticipant, occurrenceDate?: string): CalendarEventParticipantResponse {
	const entry = findOccurrenceEntry(participant, occurrenceDate);
	if (entry) return entry.rsvp;
	return participant.rsvp || CalendarEventParticipantResponse.None;
}

/** The note that applies on one date, under the same fallback rule as the answer. */
export function participantNoteOn(participant: CalendarEventParticipant, occurrenceDate?: string): string {
	const entry = findOccurrenceEntry(participant, occurrenceDate);
	if (entry) return entry.note || '';
	return participant.note || '';
}

function findOccurrenceEntry(participant: CalendarEventParticipant, occurrenceDate?: string) {
	if (!participant || !occurrenceDate) return undefined;
	if (!Array.isArray(participant.occurrenceRsvps)) return undefined;
	return participant.occurrenceRsvps.find(entry => entry.date === occurrenceDate);
}
